import type { BenefitKind, CampaignBenefit, LoyaltyCampaign } from '../api/use-loyalty-admin';

/**
 * Estado do formulário de campanha, e a tradução dos dois lados (API → form
 * para edição, form → payload para salvar).
 *
 * Extraído do componente de propósito: são funções puras, testáveis sem
 * montar React — mesmo padrão de `campaigns/model/campaign-mapper.ts`.
 */

/** Modo de exibição do desconto no cupom — ver `discountMode`. */
export const DISCOUNT_MODES = [
  { value: 'item', label: 'No item', hint: 'o desconto aparece no produto beneficiado' },
  { value: 'sale', label: 'Na venda', hint: 'o desconto aparece no total da venda' },
] as const;
export type DiscountMode = (typeof DISCOUNT_MODES)[number]['value'];

/**
 * `value`/`maxQuantity` como texto, não número: um `<input>` controlado que
 * guarda `Number(e.target.value)` no state perde o ponto decimal a cada
 * tecla (`Number("5.")` vira `5`, então o "." digitado nunca aparece).
 * Convertido pra número só na fronteira com a API, em `formToPayload`.
 */
export interface BenefitDraft {
  productCode: string;
  kind: BenefitKind;
  value: string;
  maxQuantity: string;
}

export interface FormState {
  campaignId: string;
  active: boolean;
  name: string;
  categoryName: string;
  descriptionCampaign: string;
  descriptionCommercial: string;
  image: string;
  startDate: string;
  endDate: string;
  benefits: BenefitDraft[];
  channels: number[];
  paymentTypes: number[];
  storeCodes: string[];
  order: string;
  idConflict: string;
  cumulative: boolean;
  isGift: boolean;
  /**
   * `applyDiscountOnSale` e `discountAtItem` descrevem a MESMA decisão (onde
   * o desconto aparece no cupom), então viram um controle só na tela — duas
   * chaves independentes deixariam o formulário representar um estado que
   * não existe (as duas marcadas, ou as duas desmarcadas).
   */
  discountMode: DiscountMode;
  requiresEnrollment: boolean;
  isExclusive: boolean;
  promocode: string;
  usageLimitPerCustomer: string;
}

export const EMPTY_BENEFIT: BenefitDraft = {
  productCode: '',
  kind: 'fixed_price',
  value: '',
  maxQuantity: '',
};

/** `YYYY-MM-DD` de hoje, no fuso local — igual ao que o `DatePicker` espera. */
function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function emptyForm(): FormState {
  return {
    campaignId: '',
    active: true,
    name: '',
    categoryName: '',
    descriptionCampaign: '',
    descriptionCommercial: '',
    image: '',
    startDate: todayYmd(),
    endDate: '',
    benefits: [{ ...EMPTY_BENEFIT }],
    channels: [],
    paymentTypes: [],
    storeCodes: [],
    order: '0',
    idConflict: '0',
    cumulative: false,
    isGift: false,
    discountMode: 'item',
    requiresEnrollment: false,
    isExclusive: false,
    promocode: '',
    usageLimitPerCustomer: '',
  };
}

/** Carrega uma campanha existente (edição) no formato do formulário. */
export function campaignToForm(campaign: LoyaltyCampaign): FormState {
  return {
    campaignId: String(campaign.campaignId),
    active: campaign.active,
    name: campaign.name,
    categoryName: campaign.categoryName ?? '',
    descriptionCampaign: campaign.descriptionCampaign ?? '',
    descriptionCommercial: campaign.descriptionCommercial ?? '',
    image: campaign.image ?? '',
    startDate: campaign.startDate ? campaign.startDate.slice(0, 10) : todayYmd(),
    endDate: campaign.endDate ? campaign.endDate.slice(0, 10) : '',
    benefits: campaign.benefits?.length
      ? campaign.benefits.map((b) => ({
          productCode: b.productCode,
          kind: b.kind,
          value: String(b.value),
          maxQuantity: b.maxQuantity == null ? '' : String(b.maxQuantity),
        }))
      : [{ ...EMPTY_BENEFIT }],
    channels: campaign.channels ?? [],
    paymentTypes: campaign.paymentTypes ?? [],
    storeCodes: campaign.storeCodes ?? [],
    order: String(campaign.order ?? 0),
    idConflict: String(campaign.idConflict ?? 0),
    cumulative: Boolean(campaign.cumulative),
    isGift: Boolean(campaign.isGift),
    discountMode: campaign.applyDiscountOnSale ? 'sale' : 'item',
    requiresEnrollment: Boolean(campaign.requiresEnrollment),
    isExclusive: Boolean(campaign.isExclusive),
    promocode: campaign.promocode ?? '',
    usageLimitPerCustomer:
      campaign.usageLimitPerCustomer == null ? '' : String(campaign.usageLimitPerCustomer),
  };
}

/**
 * Monta o payload de salvar.
 *
 * O save do backend substitui o documento inteiro (não é PATCH): todo campo
 * ausente no body vira o default do controller. Por isso manda `active`,
 * `startDate`, `descriptionCommercial`, `image` e o par
 * `applyDiscountOnSale`/`discountAtItem` SEMPRE, mesmo sem alteração — um
 * form anterior que omitia esses campos reativava campanha pausada,
 * empurrava a vigência pra hoje e apagava descrição comercial/imagem
 * carregadas por import em lote.
 */
export function formToPayload(form: FormState): Partial<LoyaltyCampaign> {
  const benefits: CampaignBenefit[] = form.benefits
    .filter((b) => b.productCode.trim())
    .map((b) => ({
      productCode: b.productCode.trim(),
      kind: b.kind,
      value: Number(b.value) || 0,
      maxQuantity: b.maxQuantity.trim() === '' ? null : Number(b.maxQuantity) || null,
    }));

  return {
    campaignId: Number(form.campaignId),
    active: form.active,
    name: form.name.trim(),
    categoryName: form.categoryName.trim(),
    descriptionCampaign: form.descriptionCampaign.trim(),
    descriptionCommercial: form.descriptionCommercial.trim(),
    image: form.image.trim() || null,
    startDate: form.startDate || todayYmd(),
    endDate: form.endDate || null,
    channels: form.channels,
    paymentTypes: form.paymentTypes,
    storeCodes: form.storeCodes,
    order: Number(form.order) || 0,
    idConflict: Number(form.idConflict) || 0,
    cumulative: form.cumulative,
    isGift: form.isGift,
    applyDiscountOnSale: form.discountMode === 'sale',
    discountAtItem: form.discountMode === 'item',
    requiresEnrollment: form.requiresEnrollment,
    isExclusive: form.isExclusive,
    promocode: form.promocode.trim() || null,
    usageLimitPerCustomer: form.usageLimitPerCustomer ? Number(form.usageLimitPerCustomer) : null,
    benefits,
  };
}

/** @returns mensagem de erro, ou `null` se os dados básicos estão ok. */
export function validateBasics(form: FormState): string | null {
  if (!form.campaignId.trim() || !Number.isFinite(Number(form.campaignId))) {
    return 'Informe o código numérico da campanha (é o identificador que volta para o caixa).';
  }
  if (!form.name.trim()) return 'Informe o nome da campanha.';
  return null;
}

/** @returns mensagem de erro, ou `null` se ao menos um benefício está preenchido. */
export function validateBenefits(benefits: BenefitDraft[]): string | null {
  const filled = benefits.filter((b) => b.productCode.trim());
  if (filled.length === 0) {
    return 'Sem benefício a campanha nunca aplicaria desconto: informe ao menos um produto.';
  }
  for (const b of filled) {
    if (!Number.isFinite(Number(b.value))) return 'Todo benefício precisa de um valor numérico.';
  }
  return null;
}

export function addBenefit(benefits: BenefitDraft[]): BenefitDraft[] {
  return [...benefits, { ...EMPTY_BENEFIT }];
}

export function removeBenefit(benefits: BenefitDraft[], index: number): BenefitDraft[] {
  return benefits.filter((_, i) => i !== index);
}

export function updateBenefit(
  benefits: BenefitDraft[],
  index: number,
  patch: Partial<BenefitDraft>,
): BenefitDraft[] {
  return benefits.map((b, i) => (i === index ? { ...b, ...patch } : b));
}

export function benefitKindLabel(kind: BenefitKind, kinds: readonly { value: string; label: string }[]): string {
  return kinds.find((k) => k.value === kind)?.label ?? kind;
}

/** Alterna um valor numa lista de seleção múltipla (canais, pagamentos, lojas). */
export function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}
